/**
 * AI chat assistant API (plain JSON, not JSON:API).
 * All calls go through apiClient so the tenant header and session cookie apply.
 */
import apiClient from '@/api/client'

const base = '/ai'

/** React Query key for `fetchStatus`, shared by every page that reads it. */
export const AI_STATUS_QUERY_KEY = 'ai-status'

export async function fetchStatus() {
  const { data } = await apiClient.get(`${base}/status/`)
  return data
}

/** React Query key for `fetchAiSettings`. */
export const AI_ASSISTANT_SETTINGS_QUERY_KEY = 'ai-assistant-settings'

/**
 * `GET /api/ai/settings/` (any member): `{ai_rock_writes_enabled, rock_writes:
 * {enabled, can_enable}}`. `can_enable` is true for administrators and owners.
 */
export async function fetchAiSettings() {
  const { data } = await apiClient.get(`${base}/settings/`)
  return data
}

/** `PATCH /api/ai/settings/` (administrator): same response shape as `fetchAiSettings`. */
export async function updateAiSettings({ ai_rock_writes_enabled }) {
  const { data } = await apiClient.patch(`${base}/settings/`, { ai_rock_writes_enabled })
  return data
}

export async function fetchStarters({ page, dashboardId } = {}) {
  const params = {}
  if (page) params.page = page
  if (dashboardId) params.dashboard_id = dashboardId
  const { data } = await apiClient.get(`${base}/starters/`, { params })
  return data.prompts || []
}

/**
 * Page-aware suggestion chips (Task A8, backend): `{chips: [{label, prompt,
 * metric_id, direction}]}`. On the integration-onboarding pages (A11:
 * `page` = `integrations | measurements | getting_started`, plus
 * `connection_id` on the measurements board) the chips are `{label, prompt,
 * action, template_key?}` instead. Callers should treat a failure (404 while A8
 * hasn't shipped yet, or any other error) as "no suggestions" rather than
 * surfacing it — this is a nice-to-have above the composer, never something
 * that should block opening the panel.
 */
export async function fetchSuggestions({ dashboardId, metricId, page, connectionId } = {}) {
  const params = {}
  if (dashboardId) params.dashboard_id = dashboardId
  if (metricId) params.metric_id = metricId
  if (page) params.page = page
  if (connectionId) params.connection_id = connectionId
  const { data } = await apiClient.get(`${base}/suggestions/`, { params })
  return data.chips || []
}

export async function listSessions() {
  const { data } = await apiClient.get(`${base}/sessions/`)
  return data.sessions || []
}

/**
 * @param {object} [context] - the chat context (`buildChatContext`)
 * @param {'onboarding'} [purpose] - asks for setup help; the backend decides what the session gets (`session.purpose`)
 */
export async function createSession(context = {}, purpose) {
  const { data } = await apiClient.post(`${base}/sessions/`, purpose ? { context, purpose } : { context })
  return data.session
}

export async function fetchSession(sessionId) {
  const { data } = await apiClient.get(`${base}/sessions/${sessionId}/`)
  return data
}

export async function archiveSession(sessionId) {
  await apiClient.delete(`${base}/sessions/${sessionId}/`)
}

export async function fetchMessages(sessionId, after) {
  const params = after != null ? { after } : {}
  const { data } = await apiClient.get(`${base}/sessions/${sessionId}/messages/`, { params })
  return data
}

export async function sendMessage(sessionId, text, context) {
  const payload = context ? { text, context } : { text }
  const { data } = await apiClient.post(`${base}/sessions/${sessionId}/messages/`, payload)
  return data
}

export async function cancelTurn(turnId) {
  const { data } = await apiClient.post(`${base}/turns/${turnId}/cancel/`, {})
  return data
}

export async function confirmDraft(draftId) {
  const { data } = await apiClient.post(`${base}/drafts/${draftId}/confirm/`, {})
  return data
}

export async function rejectDraft(draftId, reason = '') {
  const { data } = await apiClient.post(`${base}/drafts/${draftId}/reject/`, { reason })
  return data
}

export async function sendFeedback(messageId, { is_positive, reason_code, comment }) {
  const { data } = await apiClient.post(`${base}/messages/${messageId}/feedback/`, {
    is_positive,
    ...(reason_code ? { reason_code } : {}),
    ...(comment ? { comment } : {}),
  })
  return data
}

export async function purgeHistory() {
  const { data } = await apiClient.delete(`${base}/sessions/`)
  return data
}
